<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')" />
    </template>

    <template #content>
      <v-row no-gutters>
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda" class="">mdi-lock</v-icon>
            </div>
            <span class="text-h5">{{
              $t('message.pages.myAccount.myAccount')
            }}</span>
            <span class="caption ml-3 text--secondary">
              | {{ $t('message.pages.myAccount.myAccountSubtitle') }}</span
            >
          </v-row>
        </v-col>
        <v-col class="mt-10 pa-0 pr-5" cols="12">
          <v-row align="center">
            <v-col cols="2" style="min-width: 180px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.myAccount.changeEmail')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <template>
            <v-row>
              <v-col cols="12" class="py-0 mb-2">
                <v-card width="100%" flat style="min-height: 40px">
                  <template
                    v-if="emailSuccessfulChanged && canShowEmailValidation"
                  >
                    <v-alert dense text class="ma-0" type="success">
                      {{ $t('message.pages.myAccount.successfulEmail') }}
                    </v-alert>
                    <small style="color: (--v-primary-base)">
                      {{ $t('message.pages.myAccount.forgetEmailSession') }}
                      <a
                        href="#"
                        style="text-decoration: none"
                        @click="handleOpenForgetEmailSessions"
                      >
                        {{ $t('message.pages.myAccount.forgetEmailSession2') }}
                        &raquo;
                      </a>
                    </small>
                  </template>
                </v-card>
              </v-col>
            </v-row>
            <v-card
              class="mt-3"
              :class="canShowEmailValidation ? 'pa-4' : ''"
              outlined
              width="100%"
            >
              <v-row no-gutters v-if="canShowEmailValidation">
                <v-col cols="12" class="pb-2">
                  <b>{{ $t('message.pages.myAccount.emailRequires') }}</b>
                </v-col>
                <v-col cols="12" class="requirementsText">
                  <v-row no-gutters>
                    <v-icon
                      :color="
                        userInfo['email'] !== changeEmail.email &&
                        changeEmail.email
                          ? 'green'
                          : 'grey'
                      "
                      small
                    >
                      mdi-check-circle
                    </v-icon>

                    <span class="ml-2"
                      >{{
                        $t(
                          'message.pages.myAccount.emailRequirements.differentFromCurrent'
                        )
                      }}
                      (<code>{{ userInfo['email'] }}</code
                      >)</span
                    >
                  </v-row>
                </v-col>
                <v-col cols="12" class="requirementsText">
                  <v-row no-gutters>
                    <v-icon
                      :color="
                        areEmailsEqual && changeEmail.email ? 'green' : 'grey'
                      "
                      small
                    >
                      mdi-check-circle
                    </v-icon>

                    <span class="ml-2">{{
                      $t('message.emailsMustBeEqual')
                    }}</span>
                  </v-row>
                </v-col>
                <v-col cols="12" class="pb-0 requirementsText">
                  <v-row no-gutters>
                    <v-icon :color="isEmailValid ? 'green' : 'grey'" small>
                      mdi-check-circle
                    </v-icon>

                    <span class="ml-2"
                      >{{
                        $t('message.pages.myAccount.emailRequirements.valid')
                      }}
                    </span>
                  </v-row>
                </v-col>
              </v-row>
              <v-skeleton-loader
                v-else
                height="151"
                type="image"
              ></v-skeleton-loader>
            </v-card>
            <v-row no-gutters class="mt-6">
              <v-col cols="12">
                <v-text-field
                  v-model="changeEmail.email"
                  class="mt-3"
                  autocomplete="false"
                  outlined
                  dense
                  prepend-inner-icon="mdi-email"
                  :label="$t('message.pages.myAccount.newEmail')"
                  type="email"
                  tabindex="1"
                  :disabled="changeEmail.loadingUpdate"
                  @keyup.enter="$refs['confirmEmailIpt'].focus()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  ref="confirmEmailIpt"
                  class="mt-3"
                  v-model="changeEmail.confirmEmail"
                  autocomplete="false"
                  outlined
                  dense
                  prepend-inner-icon="mdi-email"
                  :label="$t('message.pages.myAccount.confirmNewEmail')"
                  type="email"
                  tabindex="1"
                  :disabled="changeEmail.loadingUpdate"
                  @keyup.enter="
                    openChangeEmail = !(changeEmail.loading || !isEmailValid)
                  "
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" class="mt-8 pt-0">
                <v-btn
                  large
                  block
                  :disabled="!canChangeEmail"
                  :loading="changeEmail.loadingUpdate"
                  color="primary"
                  @click="openChangeEmail = true"
                >
                  <span class="mx-12">{{
                    $t('message.pages.myAccount.changeEmail')
                  }}</span>
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <v-row align="center">
            <v-col cols="2" style="min-width: 180px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.myAccount.changePassword')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0 mb-2">
              <v-card width="100%" height="40px" flat>
                <v-alert
                  v-if="passwordSuccessfulChanged"
                  dense
                  text
                  type="success"
                >
                  {{ $t('message.pages.myAccount.successfulPassword') }}
                </v-alert>
              </v-card>
            </v-col>
          </v-row>
          <v-row no-gutters class="justify-center align-center mt-3">
            <v-col cols="12">
              <v-card class="pa-4" outlined width="100%">
                <v-row no-gutters>
                  <v-col cols="12" class="pb-2">
                    <b>{{ $t('message.pages.login.itMustHave') }}</b>
                  </v-col>
                  <v-col cols="12" class="requirementsText">
                    <v-row no-gutters>
                      <v-icon
                        :color="checkPasswordHasNumber ? 'green' : 'grey'"
                        small
                      >
                        mdi-check-circle
                      </v-icon>

                      <span class="ml-2">{{
                        $t('message.pages.login.numbers')
                      }}</span>
                    </v-row>
                  </v-col>
                  <v-col cols="12" class="requirementsText">
                    <v-row no-gutters>
                      <v-icon
                        :color="checkMinimunCharacters ? 'green' : 'grey'"
                        small
                      >
                        mdi-check-circle
                      </v-icon>

                      <span class="ml-2">{{
                        $t('message.pages.login.minCharacters')
                      }}</span>
                    </v-row>
                  </v-col>
                  <v-col cols="12" class="requirementsText">
                    <v-row no-gutters>
                      <v-icon
                        :color="checkSpecialCharacters ? 'green' : 'grey'"
                        small
                      >
                        mdi-check-circle
                      </v-icon>

                      <span class="ml-2">{{
                        $t('message.pages.login.specialCharacters')
                      }}</span>
                    </v-row>
                  </v-col>
                  <v-col cols="12" class="requirementsText">
                    <v-row no-gutters>
                      <v-icon :color="checkLetters ? 'green' : 'grey'" small>
                        mdi-check-circle
                      </v-icon>

                      <span class="ml-2">{{
                        $t('message.pages.login.differentLetters')
                      }}</span>
                    </v-row>
                  </v-col>
                  <v-col cols="12" class="requirementsText pb-0">
                    <v-row no-gutters>
                      <v-icon
                        :color="checkPasswordAreEquals ? 'green' : 'grey'"
                        small
                      >
                        mdi-check-circle
                      </v-icon>

                      <span class="ml-2">{{
                        $t('message.pages.login.confirmPass')
                      }}</span>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <v-col cols="12" class="mt-6">
              <v-text-field
                v-model="changePass.oldPassword"
                class="mt-2"
                autocomplete="false"
                outlined
                dense
                prepend-inner-icon="mdi-lock"
                :label="$t('message.pages.myAccount.currentPassword')"
                :append-icon="showOldPass ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showOldPass ? 'text' : 'password'"
                @click:append="showOldPass = !showOldPass"
                tabindex="1"
                :messages="changeOldPasswordErrorMessage"
                :error="changePasswordError.invalidOldPass"
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="changePasswordError.invalidOldPass"
                  >
                    <v-icon color="red" size="18" class="pb-1">
                      mdi-alert-circle</v-icon
                    >
                    {{ message }}
                  </span>
                </template>
              </v-text-field>
              <v-text-field
                v-model="changePass.password"
                class="mt-2"
                autocomplete="false"
                dense
                outlined
                prepend-inner-icon="mdi-lock"
                :label="$t('message.newPassword')"
                :append-icon="showChangePass ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showChangePass ? 'text' : 'password'"
                @click:append="showChangePass = !showChangePass"
                tabindex="1"
              ></v-text-field>
              <v-card flat>
                <v-text-field
                  v-model="changePass.repeatPassword"
                  class="mt-2"
                  autocomplete="false"
                  outlined
                  dense
                  prepend-inner-icon="mdi-lock"
                  :label="$t('message.repeatPassword')"
                  :messages="changePasswordErrorMessage"
                  :error="
                    changePasswordError.minError ||
                    changePasswordError.notSafeError ||
                    changePasswordError.invalidPassword ||
                    changePasswordError.limitExceeded
                  "
                  :append-icon="
                    showChangePassRepeat ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  :type="showChangePassRepeat ? 'text' : 'password'"
                  @click:append="showChangePassRepeat = !showChangePassRepeat"
                  @keyup.enter="handleChangePass"
                  tabindex="2"
                >
                  <template v-slot:message="{ message }">
                    <span
                      class="red--text caption align-text"
                      v-if="
                        changePasswordError.minError ||
                        changePasswordError.notSafeError ||
                        changePasswordError.invalidPassword ||
                        changePasswordError.limitExceeded
                      "
                    >
                      <v-icon color="red" size="18" class="pb-1">
                        mdi-alert-circle</v-icon
                      >
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
                <v-checkbox
                  v-model="forgetPasswordSessions"
                  :label="$t('message.pages.login.logoutSessions')"
                  class="mt-0 mb-2 pa-0"
                >
                </v-checkbox>
              </v-card>
            </v-col>
            <v-col cols="12" class="mt-0 pt-0">
              <v-btn
                large
                block
                :disabled="!isSafePassword || changePasswordLoading"
                color="primary"
                @click="handleChangePass"
                :loading="changePasswordLoading"
              >
                <span class="mx-12">{{
                  $t('message.pages.login.changeMyPass')
                }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <ConfirmChangeEmail
        :isFetching="changeEmail.loading"
        :openConfirm="openChangeEmail"
        @close="handleCloseConfirmChangeEmail"
        @confirm="handleChangeEmail"
        :changeEmailError="changeEmailError"
        :doneState="doneStateConfirmChangeEmail"
        :confirmLogoutSessions="confirmLogoutSessions"
        :doneStateForgetSession="doneStateForgetSession"
      />
    </template>
  </page-view>
</template>

<script>
// external imports

// internal imports
import { mapActions, mapMutations, mapState } from 'vuex'
import i18n from '@/translations/lang.js'

// API

// Components
import PageView from '@/layout/PageView.vue'
import ConfirmChangeEmail from '@/components/ConfirmChangeEmail.vue'

export default {
  components: {
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    ConfirmChangeEmail,
  },
  data() {
    return {
      isBeta: false,
      confirmLogoutSessions: false,
      forgetPasswordSessions: true,
      forgetEmailSessions: true,
      emailSuccessfulChanged: false,
      openChangeEmail: false,
      doneStateForgetSession: false,
      doneStateConfirmChangeEmail: false,
      validEmail: [
        (v) => {
          if (v && /^[\w-+.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(v)) return true
          return i18n.t('message.invalidEmail')
        },
      ],
      loading: false,
      showOldPass: false,
      showChangePass: false,
      showChangePassRepeat: false,
      showChangeEmailPass: false,
      changePasswordLoading: false,
      passwordSuccessfulChanged: false,
      changePasswordError: {
        minError: false,
        notSafeError: false,
        invalidPassword: false,
        limitExceeded: false,
        expiredCode: false,
        invalidOldPass: false,
      },
      changeEmailError: {
        generalError: false,
        invalidEmail: false,
        invalidPass: false,
        customErrorMessage: '',
      },
      changeEmail: {
        email: '',
        password: '',
        confirmEmail: '',
        loading: false,
        loadingUpdate: false,
      },
      changePass: {
        oldPassword: '',
        password: '',
        repeatPassword: '',
      },
      apikey: {
        apiKey: '',
        open: false,
        showKey: false,
        loading: false,
        getLoading: false,
      },
    }
  },
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userPlanInfo.user,
    }),
    canShowEmailValidation() {
      return (
        this.userInfo &&
        this.userInfo.hasOwnProperty('email') &&
        !this.changeEmail.loadingUpdate
      )
    },
    canChangeEmail() {
      return (
        this.isEmailValid &&
        !this.changeEmail.loadingUpdate &&
        this.areEmailsEqual
      )
    },
    areEmailsEqual() {
      return this.changeEmail.email === this.changeEmail.confirmEmail
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
    changeOldPasswordErrorMessage() {
      const i18n = this.$i18next
      if (this.changePasswordError.invalidOldPass) {
        return i18n.t('message.invalidPassword')
      }
      return ''
    },
    changePasswordErrorMessage() {
      const i18n = this.$i18next
      if (this.changePasswordError.minError) {
        return i18n.t('message.errors.minError')
      }
      if (
        this.changePasswordError.notSafeError ||
        this.changePasswordError.invalidPassword
      ) {
        return i18n.t('message.errors.notSafeError')
      }
      if (this.changePasswordError.limitExceeded) {
        return i18n.t('message.errors.limitExceeded')
      }
      if (this.changePasswordError.expiredCode) {
        return i18n.t('message.errors.expiredCode')
      }
      return ''
    },
    checkPasswordHasNumber() {
      const regex = new RegExp('^((?=.*[0-9]))')
      const regexTest = regex.test(this.changePass.password)
      return regexTest
    },
    checkMinimunCharacters() {
      const regex = new RegExp('^((?=.{8,}))')
      const regexTest = regex.test(this.changePass.password)
      return regexTest
    },
    checkSpecialCharacters() {
      const regex = new RegExp('^((?=.*[!@#$%^&*]))')
      const regexTest = regex.test(this.changePass.password)
      return regexTest
    },
    checkPasswordAreEquals() {
      return (
        this.changePass.password &&
        this.changePass.password === this.changePass.repeatPassword
      )
    },
    checkLetters() {
      const regex = new RegExp('^((?=.*[a-z])(?=.*[A-Z]))')
      const regexTest = regex.test(this.changePass.password)
      return regexTest
    },
    isSafePassword() {
      return (
        this.checkPasswordHasNumber &&
        this.checkMinimunCharacters &&
        this.checkSpecialCharacters &&
        this.checkLetters &&
        this.changePass.password === this.changePass.repeatPassword
      )
    },
    isEmailValid() {
      return (
        this.validEmail[0](this.changeEmail.email) === true &&
        this.changeEmail.email !== this.userInfo.email
      )
    },
  },
  async mounted() {
    this.isBeta = location.search.match('beta')
    const splittedHref = window.location.href.split('?')
    const params = this.paramsToObject(
      new URLSearchParams(splittedHref[splittedHref.length - 1])
    )
    this.handleThreatParams(params)
  },
  watch: {
    openChangeEmail(val) {
      if (!val) this.confirmLogoutSessions = false
    },
  },
  methods: {
    ...mapActions('session', [
      'signInUser',
      'updateEmailAddress',
      'confirmUpdateEmailAddress',
    ]),
    ...mapActions('user', ['updateUser']),
    ...mapMutations('user', ['UPDATE_USER_PLAN_INFO_ATTRIBUTES']),
    async handleThreatParams(params) {
      if (params.uuid) {
        this.changeEmail.loadingUpdate = true
        try {
          await this.confirmUpdateEmailAddress(params.uuid)
          this.$root.$emit('refreshUserPlan')
          this.$root.$on('showAccountWarning', () => {
            this.emailSuccessfulChanged = true
            this.changeEmail.loadingUpdate = false
          })
        } catch (e) {
          console.error(e)
          this.changeEmail.loadingUpdate = false
        }
      }
    },
    paramsToObject(entries) {
      const result = {}
      for (const [key, value] of entries) {
        result[key] = value
      }
      return result
    },
    handleOpenForgetEmailSessions() {
      this.confirmLogoutSessions = true
      this.openChangeEmail = true
    },
    handleCloseConfirmChangeEmail() {
      this.openChangeEmail = false
      this.doneStateForgetSession = false
      this.changeEmailError.invalidPass = false
      this.changeEmailError.generalError = false
      this.changeEmailError.customErrorMessage = ''
      this.doneStateConfirmChangeEmail = false
    },
    async handleChangeEmail(password) {
      this.changeEmailError.invalidPass = false
      this.changeEmailError.generalError = false
      try {
        if (!this.isEmailValid && !this.confirmLogoutSessions) return
        if (this.changeEmail.email === this.userInfo.email) {
          return
        }

        this.changeEmail.loading = true
        await this.signInUser({
          username: this.userInfo.email,
          password,
        })

        const { email: newEmail } = this.changeEmail

        if (this.confirmLogoutSessions) {
          await this.$store.dispatch('session/forgetSessions', true)
          await this.handleLogin(password)
          this.doneStateForgetSession = true
        } else {
          await this.updateEmailAddress({
            newEmail,
          })
          this.doneStateConfirmChangeEmail = true
          this.emailSuccessfulChanged = false
        }

        this.confirmLogoutSessions = false
      } catch (err) {
        console.error(err)
        if (err?.code === 'NotAuthorizedException') {
          // padrão de erro do cognito
          this.changeEmailError.invalidPass = true
        } else if (err?.response?.data?.errMsg) {
          // padrão de erro da nossa API
          this.changeEmailError.customErrorMessage = err.response.data.errMsg
        } else {
          this.changeEmailError.generalError = true
        }
      }
      this.changeEmail.loading = false
      this.changeEmail.loadingUpdate = false
    },
    trimAllFields() {
      Object.keys(this.changePass).forEach(
        (e) => (this.changePass[e] = this.changePass[e].trim())
      )
    },
    copyTextToClipboard() {
      if (!this.apiKey) {
        return
      }
      navigator.clipboard.writeText(`${this.apikey.apiKey}`)
    },
    async handleChangePass() {
      this.trimAllFields()
      this.passwordSuccessfulChanged = false
      Object.keys(this.changePasswordError).forEach(
        (e) => (this.changePasswordError[e] = false)
      )
      if (
        this.changePass.password === '' ||
        this.changePass.repeatPassword === ''
      ) {
        this.changePasswordError.notSafeError = true
        this.changePasswordLoading = false
        return
      }
      if (
        this.changePass.password.length < 8 ||
        this.changePass.repeatPassword < 8
      ) {
        this.changePasswordError.minError = true
        this.changePasswordLoading = false
        return
      }
      try {
        this.changePasswordLoading = true
        await this.$store.dispatch('session/changePassword', {
          newPass: this.changePass.password,
          oldPass: this.changePass.oldPassword,
        })
        if (this.forgetPasswordSessions) {
          await this.$store.dispatch('session/forgetSessions', true)
          await this.handleLogin()
        }
        Object.keys(this.changePass).forEach((e) => (this.changePass[e] = ''))
        this.passwordSuccessfulChanged = true
      } catch (error) {
        console.error(error)
        if (error.code === 'NotAuthorizedException') {
          this.changePasswordError.invalidOldPass = true
        }
        if (error.code === 'LimitExceededException') {
          this.changePasswordError.limitExceeded = true
        }
        this.changePasswordLoading = false
      }
      this.changePasswordLoading = false
    },
    async handleLogin(customPassword = null) {
      try {
        const loginData = await this.signInUser({
          username: this.userInfo.email,
          password: customPassword ?? this.changePass.password,
        })
        this.updateUser({
          email: loginData.attributes.email,
          id: loginData.attributes.sub,
          name: loginData.attributes.name ?? loginData.attributes.email,
        })
      } catch (err) {
        console.error('LOGIN ', err)
      }
    },
  },
}
</script>

<style scoped>
>>> .page-view__content {
  overflow-y: hidden !important;
}
</style>

<style>
.icon-container {
  background-color: #eaedf6;
  padding: 4px;
  border-radius: 3px;
}
.user-avatar {
  margin-top: -50px;
}
.attributesText {
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.05em;
  color: #333333;
}
.requirementsText {
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: #878787;
  padding: 6px 0px !important;
}
.requirementsText span {
  font-size: 14px;
  color: var(--v-neutral-lighten1);
}
.codeText {
  font-weight: 600;
  font-size: 28px;
  line-height: 42px;
}
.unchecked-item {
  background: #fdedf5;
  border-radius: 20px;
  color: #ed4b9e;
}
.checked-item {
  background: #ebfbf7;
  border-radius: 20px;
  color: #31d0aa;
}
.textFieldText {
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
}
.mustHaveText {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: var(--v-neutral-lighten1);
}
.requirementsText i {
  margin-bottom: 1px;
}
</style>
